import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Button,
  Text,
  BlockStack,
  InlineStack,
  EmptyState,
  Spinner,
  Modal,
  Toast,
  Badge,
  TextField,
  Box,
  Divider,
} from '@shopify/polaris';
import { api } from '../api';

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailModal, setDetailModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/products/orders');
      setOrders(res.data?.data || []);
    } catch {
      setToast({ content: 'Failed to load orders', error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const filtered = orders.filter(o =>
    !searchValue ||
    o.order_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
    o.order_id?.toLowerCase().includes(searchValue.toLowerCase())
  );

  const rows = filtered.map(order => {
    let configs = [];
    try {
      configs = typeof order.configurations === 'string'
        ? JSON.parse(order.configurations)
        : (order.configurations || []);
    } catch { configs = []; }

    return [
      <Text variant="bodyMd" fontWeight="semibold">{order.order_name || `#${order.order_id}`}</Text>,
      <Text variant="bodySm" tone="subdued">{order.product_title || order.product_id}</Text>,
      <Badge>{configs.length} options</Badge>,
      <Text variant="bodySm" tone="subdued">
        {order.created_at ? new Date(order.created_at).toLocaleDateString() : '—'}
      </Text>,
      <Button size="slim" onClick={() => setDetailModal({ ...order, configs })}>
        View Details
      </Button>,
    ];
  });

  const toastMarkup = toast ? (
    <Toast content={toast.content} error={toast.error} onDismiss={() => setToast(null)} />
  ) : null;

  return (
    <>
      {toastMarkup}
      <Page
        title="Order Configurations"
        subtitle="View custom options selected by customers in their orders"
      >
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <TextField
                  label=""
                  placeholder="Search by order name or ID..."
                  value={searchValue}
                  onChange={setSearchValue}
                  clearButton
                  onClearButtonClick={() => setSearchValue('')}
                  autoComplete="off"
                />

                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                    <Spinner size="large" />
                  </div>
                ) : filtered.length === 0 ? (
                  <EmptyState
                    heading="No order configurations yet"
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <p>When customers place orders with custom configurations, they'll appear here.</p>
                  </EmptyState>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                    headings={['Order', 'Product', 'Options', 'Date', 'Actions']}
                    rows={rows}
                    hoverable
                  />
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>

        {/* Order Detail Modal */}
        <Modal
          open={!!detailModal}
          onClose={() => setDetailModal(null)}
          title={`Order ${detailModal?.order_name || detailModal?.order_id} — Configuration`}
          secondaryActions={[{ content: 'Close', onAction: () => setDetailModal(null) }]}
        >
          <Modal.Section>
            {detailModal && (
              <BlockStack gap="400">
                <InlineStack gap="400" wrap>
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">Order</Text>
                    <Text variant="bodyMd" fontWeight="semibold">{detailModal.order_name}</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">Product</Text>
                    <Text variant="bodyMd">{detailModal.product_title || detailModal.product_id}</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">Date</Text>
                    <Text variant="bodyMd">
                      {detailModal.created_at ? new Date(detailModal.created_at).toLocaleString() : '—'}
                    </Text>
                  </BlockStack>
                </InlineStack>

                <Divider />

                <Text variant="headingMd" as="h3">Customer Selections</Text>

                {(detailModal.configs || []).length === 0 ? (
                  <Text tone="subdued">No configuration data available.</Text>
                ) : (
                  <BlockStack gap="200">
                    {(detailModal.configs || []).map((config, i) => (
                      <Box
                        key={i}
                        padding="300"
                        background="bg-surface-secondary"
                        borderRadius="200"
                      >
                        <InlineStack align="space-between" blockAlign="center">
                          <Text variant="bodyMd" fontWeight="semibold" tone="subdued">
                            {config.name}
                          </Text>
                          <Text variant="bodyMd">
                            {config.value || <span style={{ color: '#999' }}>—</span>}
                          </Text>
                        </InlineStack>
                      </Box>
                    ))}
                  </BlockStack>
                )}
              </BlockStack>
            )}
          </Modal.Section>
        </Modal>
      </Page>
    </>
  );
}
